<template>
  <div>
    <div class="p-tasklist-actions">
      <FormFilterTask
        v-model="params.filter"
        :items="allItems"
        :project="project"
      />
      <ButtonDownload :project="project" class="l-ml-4" />
    </div>
    <div class="p-tasklist-container">
      <div class="p-tasklist-box p-tasklist-box--todo">
        <div class="p-tasklist-box__header">
          <h2 class="p-tasklist-box__title">
            ToDoリスト
            <Label variant="count">{{ totalToDoItems }}</Label>
          </h2>
        </div>
        <div class="p-tasklist-box__inner">
          <Button
            variant="adddata"
            icon="plus_gn"
            block
            class="l-mb-3"
            @click="openAddModal('todo')"
          >
            タスクを追加
          </Button>
          <div class="p-tasklist-box__content">
            <ListTask
              column="todo"
              :items="data.items.todo"
              :project="project"
              @sort="sortTask"
              @edit="editTask"
            >
              <ListItemTask
                v-for="item in data.items.todo"
                :key="item.id"
                :item="item"
                :project="project"
                :isVisible="isMatched(item)"
                @edit="openEditModal"
                ref="item"
              />
            </ListTask>
          </div>
        </div>
      </div>
      <div class="p-tasklist-box p-tasklist-box--doing">
        <div class="p-tasklist-box__header">
          <h2 class="p-tasklist-box__title">
            進行中
            <Label variant="count">{{ totalDoingItems }}</Label>
          </h2>
        </div>
        <div class="p-tasklist-box__inner">
          <Button
            variant="adddata"
            icon="plus_gn"
            block
            class="l-mb-3"
            @click="openAddModal('doing')"
          >
            タスクを追加
          </Button>
          <div class="p-tasklist-box__content">
            <ListTask
              column="doing"
              :items="data.items.doing"
              :project="project"
              @sort="sortTask"
              @edit="editTask"
            >
              <ListItemTask
                v-for="item in data.items.doing"
                :key="item.id"
                :item="item"
                :project="project"
                :isVisible="isMatched(item)"
                @edit="openEditModal"
                ref="item"
              />
            </ListTask>
          </div>
        </div>
      </div>
      <div class="p-tasklist-box p-tasklist-box--done">
        <div class="p-tasklist-box__header">
          <h2 class="p-tasklist-box__title">
            完了
            <Label variant="count">{{ totalDoneItems }}</Label>
          </h2>
        </div>
        <div class="p-tasklist-box__inner">
          <div class="p-tasklist-box__content">
            <ListTask
              column="done"
              :items="data.items.done"
              :project="project"
              @sort="sortTask"
              @edit="editTask"
            >
              <ListItemTask
                v-for="item in data.items.done"
                :key="item.id"
                :item="item"
                :project="project"
                :isVisible="isMatched(item)"
                @openEditModal="openEditModal"
                @edit="openEditModal"
              />
            </ListTask>
          </div>
        </div>
      </div>
      <ModalFormProposal
        v-if="modal.isActiveProposal"
        :item="modal.item"
        :project="project"
        @delete="deleteTask"
        @edit="editTask"
        @close="closeModal"
      />
      <ModalFormTask
        v-if="modal.isActive"
        :column="modal.column"
        :item="modal.item"
        :mode="modal.mode"
        :project="project"
        @add="addTask"
        @delete="deleteTask"
        @edit="editTask"
        @close="closeModal"
      />
    </div>
  </div>
</template>

<script>
import Button from '@/components/Button';
import Label from '@/components/Label';
import ButtonDownload from '@/components/taskList/ButtonDownload';
import FormFilterTask from '@/components/taskList/FormFilterTask';
import ListItemTask from '@/components/taskList/ListItemTask';
import ListTask from '@/components/taskList/ListTask';
import ModalFormProposal from '@/components/taskList/ModalFormProposal';
import ModalFormTask from '@/components/taskList/ModalFormTask';
import apiProjects from '@/api/projects';
import utils from '@/utils';
import * as dayjs from 'dayjs';
import 'dayjs/locale/ja';
dayjs.locale('ja');

export default {
  props: {
    project: {
      type: Object,
      required: true
    }
  },
  components: {
    Button,
    Label,
    ButtonDownload,
    FormFilterTask,
    ListItemTask,
    ListTask,
    ModalFormProposal,
    ModalFormTask
  },
  data() {
    return {
      count: 100,
      data: {
        items: {
          todo: [],
          doing: [],
          done: []
        }
      },
      modal: {
        column: 'todo',
        item: {},
        mode: 'add',
        isActive: false,
        isActiveProposal: false
      },
      order: 'position:asc',
      params: {
        filter: {
          operatorUserId: '',
          type: '',
          keyword: '',
          url: '',
          executionDateUnit: '',
          typeDoneDateUnit: ''
        }
      },
      status: {
        all: ['todo', 'doing', 'done'],
        active: ['todo', 'doing']
      },
      isLoading: true
    };
  },
  computed: {
    allItems() {
      const items = [];
      for (let i = 0; i < this.status.all.length; i++) {
        const status = this.status.all[i];
        items.push(...this.data.items[status]);
      }
      return items;
    },
    todoItems() {
      return this.data.items.todo || [];
    },
    filteredTodoItems() {
      return this.todoItems.filter(item => {
        return this.isMatched(item);
      });
    },
    totalToDoItems() {
      return this.filteredTodoItems.length;
    },
    doingItems() {
      return this.data.items.doing || [];
    },
    filteredDoingItems() {
      return this.doingItems.filter(item => {
        return this.isMatched(item);
      });
    },
    totalDoingItems() {
      return this.filteredDoingItems.length;
    },
    doneItems() {
      return this.data.items.done || [];
    },
    filteredDoneItems() {
      return this.doneItems.filter(item => {
        return this.isMatched(item);
      });
    },
    totalDoneItems() {
      return this.filteredDoneItems.length;
    }
  },

  created() {
    this.getTasks();
    this.$store.dispatch('project/getUsers');
    this.$store.dispatch('task/getTaskSolutions');
  },
  methods: {
    addTask(task) {
      // IDの重複を防ぐ
      if (
        process.env.VUE_APP_ENV === 'local' ||
        process.env.VUE_APP_ENV === 'demo' ||
        process.env.VUE_APP_ENV === 'prototype'
      ) {
        task.id = this.count;
        this.count++;
      }
      this.data.items[task.column].unshift(task);
      this.updateTotalTask();
      this.closeModal();

      // 新しく追加したタスクをフェードインで表示する
      this.$nextTick(() => {
        const item = this.$refs.item[this.$refs.item.length - 1];
        if (item) {
          item.$el.classList.add('is-animate');
          // 同じカラム内で並び替えるたびにフェードインするのを阻止
          item.$el.addEventListener('animationend', () => {
            item.$el.classList.remove('is-animate');
          });
        }
      });
    },
    deleteTask(task) {
      const index = this.data.items[task.column].findIndex(
        item => item.id === task.id
      );
      this.data.items[task.column].splice(index, 1);
      this.updateTotalTask();
      this.closeModal();
    },
    editTask(task) {
      const index = this.data.items[task.column].findIndex(
        item => item.id === task.id
      );
      this.data.items[task.column].splice(index, 1, task);
      this.closeModal();
    },
    getTasks() {
      this.isLoading = true;

      Promise.all(
        this.status.all.map(status => {
          return apiProjects.tasks.get(
            {
              columns: status,
              order: this.order
            },
            this.auth.idToken,
            this.project.id
          );
        })
      )
        .then(response => {
          for (let i = 0; i < this.status.all.length; i++) {
            const status = this.status.all[i];
            this.data.items[status] = response[i].data.items.map(item => {
              if (!item.type) {
                item.type = 'other';
              }
              return item;
            });
          }
          this.isLoading = false;
          this.updateTotalTask();
        })
        .catch(error => {
          this.isLoading = false;
        });
    },
    sortTask(taskId, column) {
      const index = this.data.items[column].findIndex(
        item => item.id === taskId
      );
      const task = Object.assign({}, this.data.items[column][index]);
      task.column = column;

      this.data.items[column].splice(index, 1, task);
      this.updateTotalTask();
    },
    updateTotalTask() {
      let total = 0;
      for (let i = 0; i < this.status.active.length; i++) {
        const status = this.status.active[i];
        total += this.data.items[status].length;
      }
      // totalをストアに保存（サイドバーにも表示するため）
      this.$store.dispatch('project/setTaskCount', total);
    },
    openAddModal(column) {
      this.modal.column = column;
      this.modal.mode = 'add';
      this.modal.isActive = true;
    },
    openEditModal(item) {
      this.modal.item = item;
      this.modal.mode = 'edit';
      if (item.isProposal) {
        this.modal.isActiveProposal = true;
      } else {
        this.modal.isActive = true;
      }
    },
    closeModal() {
      this.modal.column = '';
      this.modal.item = {};
      this.modal.mode = '';
      this.modal.isActive = false;
      this.modal.isActiveProposal = false;
    },
    isMatched(item) {
      return utils.isMatched(item, this.params.filter);
    }
  }
};
</script>
